import React, { useEffect, useState } from 'react';
import { Wallet, Zap, Plus, IndianRupee, RefreshCcw, ArrowDownRight, ArrowUpRight, CreditCard } from 'lucide-react';
import { walletAPI } from '@/services/walletAPI';
import { paymentsAPI } from '@/services/paymentsAPI';
import { useAuthStore } from '@/store/authStore';
import RazorpayCheckoutModal from '@/components/shared/RazorpayCheckoutModal';

/**
 * ApplicantWallet — Recharge with Razorpay + full transaction history.
 * Shows two panels: (a) INR-payment history, (b) Mu credit/debit ledger.
 */
export default function ApplicantWallet() {
  const { user } = useAuthStore();
  const [balance, setBalance] = useState(0);
  const [txns, setTxns] = useState([]);
  const [payments, setPayments] = useState([]);
  const [tab, setTab] = useState('txns'); // 'txns' | 'payments'
  const [loading, setLoading] = useState(true);
  const [rechargeOpen, setRechargeOpen] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [w, hist] = await Promise.all([
        walletAPI.me(),
        paymentsAPI.getHistory(),
      ]);
      setBalance(w?.balance ?? 0);
      setTxns(w?.recent_transactions || []);
      setPayments(hist?.records || []);
    } catch (_) { /* silent */ }
    setLoading(false);
  };

  useEffect(() => {
    refresh();
    const handler = () => refresh();
    window.addEventListener('mu-wallet-changed', handler);
    return () => window.removeEventListener('mu-wallet-changed', handler);
  }, []);

  return (
    <div data-testid="applicant-wallet">
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-white flex items-center gap-2">
          <Wallet className="w-5 h-5 text-emerald-500" /> My Wallet
        </h1>
        <button onClick={refresh} className="text-slate-500 dark:text-zinc-500 hover:text-slate-900 dark:hover:text-white"
                data-testid="wallet-refresh">
          <RefreshCcw className="w-4 h-4" />
        </button>
      </div>
      <p className="text-slate-500 dark:text-zinc-500 text-sm mb-6">
        Recharge with Razorpay and track every Mu point coming in or out.
      </p>

      {/* Balance + recharge */}
      <div className="rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 p-6 text-white mb-6 shadow-lg">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-wider opacity-80">Current balance</p>
            <p className="text-4xl font-bold mt-1 flex items-baseline gap-2" data-testid="wallet-balance">
              {balance} <span className="text-lg opacity-80">Mu</span>
            </p>
          </div>
          <button
            onClick={() => setRechargeOpen(true)}
            className="px-4 py-2.5 rounded-xl bg-white text-emerald-700 hover:bg-emerald-50 text-sm font-semibold flex items-center gap-2 shadow"
            data-testid="wallet-recharge-btn"
          >
            <Plus className="w-4 h-4" /> Recharge
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 mb-4 border-b border-slate-200 dark:border-zinc-800">
        <button
          onClick={() => setTab('txns')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            tab === 'txns'
              ? 'text-emerald-600 dark:text-emerald-400 border-emerald-500'
              : 'text-slate-500 dark:text-zinc-500 border-transparent hover:text-slate-900 dark:hover:text-white'
          }`}
          data-testid="wallet-tab-txns"
        >
          Mu Ledger ({txns.length})
        </button>
        <button
          onClick={() => setTab('payments')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            tab === 'payments'
              ? 'text-emerald-600 dark:text-emerald-400 border-emerald-500'
              : 'text-slate-500 dark:text-zinc-500 border-transparent hover:text-slate-900 dark:hover:text-white'
          }`}
          data-testid="wallet-tab-payments"
        >
          Payments ({payments.length})
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <p className="text-slate-500 dark:text-zinc-500 text-sm">Loading…</p>
      ) : tab === 'txns' ? (
        <TxnList txns={txns} />
      ) : (
        <PaymentList payments={payments} />
      )}

      <RazorpayCheckoutModal
        isOpen={rechargeOpen}
        onClose={() => setRechargeOpen(false)}
        user={{ email: user?.email, phone: user?.phone, name: user?.name }}
        onSuccess={() => refresh()}
      />
    </div>
  );
}

function TxnList({ txns }) {
  if (!txns.length) {
    return (
      <div className="rounded-lg border border-dashed border-slate-200 dark:border-zinc-800 p-8 text-center text-slate-500 dark:text-zinc-500 text-sm">
        No Mu transactions yet.
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="wallet-txn-list">
      {txns.map((t) => (
        <div key={t.id} className="flex items-center gap-3 bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-lg px-4 py-3">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
            t.kind === 'credit' ? 'bg-emerald-500/10 text-emerald-500' : 'bg-red-500/10 text-red-500'
          }`}>
            {t.kind === 'credit' ? <ArrowDownRight className="w-4 h-4" /> : <ArrowUpRight className="w-4 h-4" />}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-slate-900 dark:text-white truncate">{t.reason || '—'}</p>
            <p className="text-xs text-slate-500 dark:text-zinc-500">
              {t.created_at ? new Date(t.created_at).toLocaleString() : ''} · balance {t.balance_after} Mu
            </p>
          </div>
          <div className={`text-sm font-semibold flex items-center gap-1 ${
            t.kind === 'credit' ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'
          }`}>
            {t.kind === 'credit' ? '+' : '−'}{t.amount} <Zap className="w-3 h-3" />
          </div>
        </div>
      ))}
    </div>
  );
}

function PaymentList({ payments }) {
  if (!payments.length) {
    return (
      <div className="rounded-lg border border-dashed border-slate-200 dark:border-zinc-800 p-8 text-center text-slate-500 dark:text-zinc-500 text-sm">
        No Razorpay payments yet. Tap <b>Recharge</b> above to make your first payment.
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="wallet-payments-list">
      {payments.map((p) => (
        <div key={p.id} className="flex items-center gap-3 bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-lg px-4 py-3">
          <div className="w-8 h-8 rounded-full bg-blue-500/10 text-blue-500 flex items-center justify-center flex-shrink-0">
            <CreditCard className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-slate-900 dark:text-white">Razorpay recharge · <span className="text-emerald-600 dark:text-emerald-400">+{p.mu_points_credited} Mu</span></p>
            <p className="text-xs text-slate-500 dark:text-zinc-500 truncate">
              {p.created_at ? new Date(p.created_at).toLocaleString() : ''} · rate ₹{p.conversion_rate_inr_per_mu}/Mu · {p.razorpay_payment_id}
            </p>
          </div>
          <div className="text-sm font-semibold text-slate-900 dark:text-white flex items-center gap-0.5">
            <IndianRupee className="w-3.5 h-3.5" />{p.amount_inr}
          </div>
        </div>
      ))}
    </div>
  );
}
